// ConflictDialog — what a 409 `DOC_REVISION_CONFLICT` looks like to the editor.
// The save guard rejects when the doc moved on (someone else — or an agent —
// saved a revision) or when our lease is no longer the live one. The proxy
// returns the current revision id plus a 4 KiB excerpt of the server's content
// so the editor can re-read and merge rather than blindly overwrite.

import { AlertTriangle } from 'lucide-react'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import type { RevisionConflict } from './doc-helpers'

export interface ConflictDialogProps {
  conflict: RevisionConflict | null
  /** Discard local edits and reload the server copy (re-acquires the lease). */
  onReload: () => void
  /** Keep editing the local draft; the next save will conflict again. */
  onDismiss: () => void
}

const ConflictDialog = ({ conflict, onReload, onDismiss }: ConflictDialogProps) => (
  <Dialog open={conflict !== null} onOpenChange={(next) => (!next ? onDismiss() : undefined)}>
    <DialogContent className="sm:max-w-140">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="size-4 text-st-warn" aria-hidden />
          This doc changed while you were editing
        </DialogTitle>
        <DialogDescription>
          Your revision was not saved. Reload the current version and re-apply your changes, or keep
          your draft open and copy the parts you need across.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-1.5">
        <span className="text-11 font-medium uppercase tracking-wider text-ink-3">
          Current version on the server
        </span>
        <pre className="m-0 max-h-64 overflow-auto rounded-md bg-bg-sunk/60 p-3 font-mono text-12 leading-relaxed text-ink-2">
          {conflict?.contentExcerpt === '' ? '(empty)' : conflict?.contentExcerpt}
        </pre>
        {conflict?.contentExcerpt !== undefined && conflict.contentExcerpt.length >= 4096 ? (
          <span className="text-11 text-ink-3">
            Excerpt truncated at 4 KiB — reload to see the whole doc.
          </span>
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDismiss}>
          Keep my draft
        </Button>
        <Button onClick={onReload}>Reload current version</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

export default ConflictDialog
